import type { SignalType } from "app/pages/sales-intelligence/domain/signals/types/signal";

/**
 * Centralized i18n key generators for `signals` module.
 */
const signalsI18nKeys = {
    name: {
        /** Joins signal name and timeframe — "%name% %timeframe%" */
        withTimeframe: "si.signals.name.with_timeframe",
    },

    /** Per-type label — "Intent", "Ecommerce", … */
    signalType: (signalType: SignalType) => `si.signals.type.${signalType}.text`,

    /** Per-type section body on the manage-signals page. */
    sectionDescription: (signalType: SignalType) => `si.signals.type.${signalType}.section.description`,

    /** Per-type label for the leads-search filter panel — "Technology signals", … (SI-9047: disambiguates from other filter categories). */
    signalTypeFilterTitle: (signalType: SignalType) => `si.signals.type.${signalType}.filter-title.text`,

    /**
     * Add-signal copy family. Shared across the create-signal modal,
     * the empty-state CTA, and the (disabled) coming-soon CTA.
     */
    addSignal: {
        cta: (signalType: SignalType) => `si.signals.type.${signalType}.add-signal.text`,
        shortCta: "si.signals.add-signal.button.text",
        description: (signalType: SignalType) => `si.signals.type.${signalType}.add-signal.description`,
        /** Generic modal body when a type has no per-type description published yet. */
        descriptionFallback: "si.signals.add-signal.description",
    },

    manageSignals: {
        cta: "si.signals.dropdown.button.manage.text",
        firstSignalCta: "si.signals.dropdown.button.manage.first.text",
        shortCta: "si.signals.dropdown.button.manage.short.text",
    },

    filters: {
        selectPlaceholder: "si.signals.filters.select.placeholder.text",
    },

    /** Per-type empty-state body. */
    emptyStateText: (signalType: SignalType) => `si.signals.type.${signalType}.empty-state.text`,

    /** Coming-soon body shown for types not yet released. */
    comingSoonText: "si.signals.coming_soon.description",

    /** Per-type signal namespace (table cells, headlines, segments, templates). */
    ecommerce: {
        tableColumnEcommerceTrend: "si.signals.ecommerce.table.column.ecommerce-trend",
        /** Leads table E-commerce trend column — PlainTooltip on each metric row */
        tableCellEcommerceTrendTooltip: {
            previousOnlineRevenueWas:
                "si.signals.ecommerce.table.column.ecommerce-trend.tooltip.previous-online-revenue-was",
            previousOnlineRevenueUnknown:
                "si.signals.ecommerce.table.column.ecommerce-trend.tooltip.previous-online-revenue-unknown",
            previousOnlineTransactionsWas:
                "si.signals.ecommerce.table.column.ecommerce-trend.tooltip.previous-online-transactions-was",
            previousOnlineTransactionsUnknown:
                "si.signals.ecommerce.table.column.ecommerce-trend.tooltip.previous-online-transactions-unknown",
        },
        signalName: {
            onlineRevenueChange: "si.signals.ecommerce.signal-name.online-revenue-change",
            onlineTransactionsChange: "si.signals.ecommerce.signal-name.online-transactions-change",
            onlineRevenueReached: "si.signals.ecommerce.signal-name.online-revenue-reached",
            onlineTransactionsReached: "si.signals.ecommerce.signal-name.online-transactions-reached",
        },
        eventHeadline: {
            onlineRevenueChange: "si.signals.ecommerce.event-headline.online-revenue-change",
            onlineRevenueChangeNoPrevious: "si.signals.ecommerce.event-headline.online-revenue-change.no-previous",
            onlineTransactionsChange: "si.signals.ecommerce.event-headline.online-transactions-change",
            onlineTransactionsChangeNoPrevious:
                "si.signals.ecommerce.event-headline.online-transactions-change.no-previous",
            onlineRevenueReached: "si.signals.ecommerce.event-headline.online-revenue-reached",
            onlineTransactionsReached: "si.signals.ecommerce.event-headline.online-transactions-reached",
        },
        directionLabel: (direction: string) => `si.signals.changing-traction-direction.${direction}.label`,
        periodLabel: (period: string) => `si.signals.changing-traction.${period}.label`,
        segments: {
            onlineRevenueChangeTitle: "si.signals.segments.ecommerce.online-revenue-change.title",
            onlineRevenueChangeDescription: "si.signals.segments.data.ecommerce.online-revenue-change",
            onlineTransactionsChangeTitle: "si.signals.segments.ecommerce.online-transactions-change.title",
            onlineTransactionsChangeDescription: "si.signals.segments.data.ecommerce.online-transactions-change",
            onlineRevenueReachedTitle: "si.signals.segments.ecommerce.online-revenue-reached.title",
            onlineRevenueReachedDescription: "si.signals.segments.data.ecommerce.online-revenue-reached",
            onlineTransactionsReachedTitle: "si.signals.segments.ecommerce.online-transactions-reached.title",
            onlineTransactionsReachedDescription: "si.signals.segments.data.ecommerce.online-transactions-reached",
        },
        templates: {
            onlineRevenueChange: {
                name: "si.signals.templates.ecommerce.online-revenue-change.name",
                description: "si.signals.templates.ecommerce.online-revenue-change.description",
            },
            onlineRevenueReached: {
                name: "si.signals.templates.ecommerce.online-revenue-reached.name",
                description: "si.signals.templates.ecommerce.online-revenue-reached.description",
            },
            onlineTransactionsChange: {
                name: "si.signals.templates.ecommerce.online-transactions-change.name",
                description: "si.signals.templates.ecommerce.online-transactions-change.description",
            },
            onlineTransactionsReached: {
                name: "si.signals.templates.ecommerce.online-transactions-reached.name",
                description: "si.signals.templates.ecommerce.online-transactions-reached.description",
            },
        },
    },
} as const;

export default signalsI18nKeys;
